import { ref } from 'vue';
import { httpService, type ApiResponse } from '../services/httpService';
import { log } from '../utils/DebugLogger';
import { config } from '../services/configService';
import { validate } from '../contracts/validate';
import '../contracts/Franquias_SalvaFranquiaInput.ts';

import '../contracts/Franquias_BuscaFranquiaResponse';
import '../contracts/Franquias_BuscaFranquiasResponse';
import '../contracts/Franquias_HealthCheckResponse';
import '../contracts/Franquias_SalvaFranquiaResponse';
import type { Franquias_SalvaFranquiaInput } from '../contracts/Franquias_SalvaFranquiaInput';
import '../contracts/Franquias_SalvarMetasInput';
import '../contracts/Franquias_SalvarOperacionalInput';
import '../contracts/Franquias_SalvarWizardInput';
import '../contracts/Franquias_SalvarWizardResponse';
import '../contracts/Franquias_SalvarMetasResponse';
import '../contracts/Franquias_SalvarOperacionalResponse';
import type { Franquias_SalvarMetasInput } from '../contracts/Franquias_SalvarMetasInput';
import type { Franquias_SalvarOperacionalInput } from '../contracts/Franquias_SalvarOperacionalInput';
import '../contracts/Franquias_SetAtivoInput';
import '../contracts/Franquias_SetAtivoResponse';
import type { Franquias_SalvarWizardInput } from '../contracts/Franquias_SalvarWizardInput';

/**
 * Working shape of the franchise-configuration payload returned by
 * /franquias/franquias/getConfiguracao. The backend `Franquias_*` contracts
 * are still shape-erased (`array $dados`), so this interface is the frontend's
 * description of the fields the wizard / metas / operacional tabs actually
 * read. The index signature keeps any other key accessible as `unknown`.
 */
interface FranquiaConfiguracao {
  cnpj?: string;
  inscricao_estadual?: string;
  endereco?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  mensagem_aniversariante?: string;
  faturamento_mensal?: number;
  faturamento_anual?: number;
  crescimento?: number;
  ticket_medio?: number;
  itens_dia?: number;
  investimento_total?: number;
  roe_desejado?: number;
  capacidade_producao_diaria?: number;
  meta_producao_itens_dia?: number;
  valor_medio_item?: number;
  [key: string]: unknown;
}

/**
 * Composable for Franquias (Franchises) operations
 * Provides methods to interact with the Franquias API
 *
 */
export function useFranquias() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Check franchise service health
   * @returns {Promise<Object>} Health status
   */
  const healthCheck = async () => {
    log('useFranquias', 'healthCheck', 'Checking service health');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`${config.api.sistemaHost}/franquias/franquias/healthCheck`, { responseContract: 'Franquias_HealthCheckResponse' });
      log('useFranquias', 'healthCheck', 'Response', response);

      if (response.dados) {
        return response.dados;
      } else {
        throw new Error('Erro ao verificar saúde do serviço');
      }
    } catch (err) {
      log('useFranquias', 'healthCheck', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch all franchises with statistics
   * @returns {Promise<Object>} { franquias: [], cidades: 0, estados: 0, paises: 0, idiomas: 0, paginas: 0 }
   */
  const fetchFranquias = async () => {
    log('useFranquias', 'fetchFranquias', 'Fetching all franchises');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/franquias/franquias/buscaFranquias', { responseContract: 'Franquias_BuscaFranquiasResponse' });
      log('useFranquias', 'fetchFranquias', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar franquias');
      }
    } catch (err) {
      log('useFranquias', 'fetchFranquias', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single franchise by ID with full details
   * @param {number} franquiaId - Franchise ID
   * @returns {Promise<Object>} { Franquia: {...}, AreasAtuacao: {...} }
   */
  const fetchFranquia = async (franquiaId: number) => {
    log('useFranquias', 'fetchFranquia', 'Fetching franchise', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/franquias/franquias/buscaFranquia/${franquiaId}`, { responseContract: 'Franquias_BuscaFranquiaResponse' });
      log('useFranquias', 'fetchFranquia', 'Response', response);

      if (response.success && response.dados) {
        // Response.Dados is an array with single franchise object
        const franquiaData = Array.isArray(response.dados) ? response.dados[0] : response.dados;
        log('useFranquias', 'fetchFranquia', 'Franchise data', franquiaData);
        return franquiaData;
      } else {
        throw new Error(response.error || 'Erro ao buscar franquia');
      }
    } catch (err) {
      log('useFranquias', 'fetchFranquia', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create or update franchise
   * @param {Object} franquiaData - Franchise data
   * @param {number} [franquiaData.ID] - If provided, updates existing franchise
   * @param {string} franquiaData.Nome - Franchise name (required)
   * @param {string} franquiaData.Email - Franchise email (required)
   * @param {string} [franquiaData.CNPJ] - Franchise CNPJ
   * @param {string} franquiaData.Pais - Country (required)
   * @param {string} franquiaData.Idioma - Language (required)
   * @returns {Promise<Object>} Saved franchise
   */
  const saveFranquia = async (franquiaData: Franquias_SalvaFranquiaInput) => {
    log('useFranquias', 'saveFranquia', 'Saving franchise', franquiaData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Franquias_SalvaFranquiaInput', franquiaData);
      const response = await httpService.post('/index.php/franquias/franquias/salvaFranquia', franquiaData, { responseContract: 'Franquias_SalvaFranquiaResponse' });
      log('useFranquias', 'saveFranquia', 'Response', response);

      if (response.success && response.dados) {
        return response.dados;
      } else {
        throw new Error(response.error || response.error || 'Erro ao salvar franquia');
      }
    } catch (err) {
      log('useFranquias', 'saveFranquia', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch franchise configuration (getConfiguracao)
   * @param {number} franquiaId - Franchise ID
   * @returns {Promise<Object>} Configuration data
   */
  const fetchConfiguracaoFranquia = async (franquiaId: number) => {
    log('useFranquias', 'fetchConfiguracaoFranquia', 'Fetching config for franchise', franquiaId);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get<ApiResponse<FranquiaConfiguracao>>(`/index.php/franquias/franquias/getConfiguracao/${franquiaId}`);
      log('useFranquias', 'fetchConfiguracaoFranquia', 'Response', response);

      if (response.success && response.dados) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao buscar configuração da franquia');
      }
    } catch (err) {
      log('useFranquias', 'fetchConfiguracaoFranquia', 'Error', err);
      error.value = (err as any).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save financial goals (metas) for a franchise
   * @param {number} franquiaId - Franchise ID
   * @param {Franquias_SalvarMetasInput} metasData - Metas data
   * @returns {Promise<Object>} Saved data
   */
  const saveMetas = async (franquiaId: number, metasData: Franquias_SalvarMetasInput) => {
    log('useFranquias', 'saveMetas', 'Saving metas for franchise', franquiaId, metasData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Franquias_SalvarMetasInput', metasData);
      const response = await httpService.post<ApiResponse>(`/index.php/franquias/franquias/salvarMetas/${franquiaId}`, metasData, {
        responseContract: 'Franquias_SalvarMetasResponse'
      });
      log('useFranquias', 'saveMetas', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao salvar metas');
      }
    } catch (err) {
      log('useFranquias', 'saveMetas', 'Error', err);
      error.value = (err as any).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save operational configuration for a franchise
   * @param {number} franquiaId - Franchise ID
   * @param {Franquias_SalvarOperacionalInput} operacionalData - Operational data
   * @returns {Promise<Object>} Saved data
   */
  const saveOperacional = async (franquiaId: number, operacionalData: Franquias_SalvarOperacionalInput) => {
    log('useFranquias', 'saveOperacional', 'Saving operational config for franchise', franquiaId, operacionalData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Franquias_SalvarOperacionalInput', operacionalData);
      const response = await httpService.post<ApiResponse>(`/index.php/franquias/franquias/salvarOperacional/${franquiaId}`, operacionalData, {
        responseContract: 'Franquias_SalvarOperacionalResponse'
      });
      log('useFranquias', 'saveOperacional', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao salvar configurações operacionais');
      }
    } catch (err) {
      log('useFranquias', 'saveOperacional', 'Error', err);
      error.value = (err as any).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save franchise wizard configuration
   * @param {number} franquiaId - Franchise ID
   * @param {Franquias_SalvarWizardInput} wizardData - Wizard data
   * @returns {Promise<Object>} Saved data
   */
  const saveWizardConfig = async (franquiaId: number, wizardData: Franquias_SalvarWizardInput) => {
    log('useFranquias', 'saveWizardConfig', 'Saving wizard config for franchise', franquiaId, wizardData);
    isLoading.value = true;
    error.value = null;

    try {
      validate('Franquias_SalvarWizardInput', wizardData);
      const response = await httpService.post(`/index.php/franquias/franquias/salvarWizard/${franquiaId}`, wizardData, { responseContract: 'Franquias_SalvarWizardResponse' });
      log('useFranquias', 'saveWizardConfig', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao salvar configuração do wizard');
      }
    } catch (err) {
      log('useFranquias', 'saveWizardConfig', 'Error', err);
      error.value = (err as any).message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Liga ou desliga uma franquia (chamados 2026-0023 e 2026-0054). Só o grupo Full pode; o
   * backend recusa os demais. Status é visibilidade na gestão, não permissão: inativar não
   * tira o acesso de ninguém nem esconde histórico.
   */
  const setAtivo = async (franquiaId: number, ativo: boolean): Promise<void> => {
    log('useFranquias', 'setAtivo', 'Setting franchise status', franquiaId, ativo);
    const response = await httpService.post<ApiResponse>(
      '/index.php/franquias/franquias/setAtivo',
      { franquia_id: franquiaId, ativo },
      { inputContract: 'Franquias_SetAtivoInput', responseContract: 'Franquias_SetAtivoResponse' },
    );
    if (!response.success) {
      throw new Error(response.error || 'Erro ao alterar o status da franquia');
    }
  };

  return {
    // State
    isLoading,
    error,

    // Methods
    healthCheck,
    fetchFranquias,
    fetchFranquia,
    saveFranquia,
    fetchConfiguracaoFranquia,
    saveMetas,
    saveOperacional,
    saveWizardConfig,
    setAtivo
  };
}
